import { useState, SyntheticEvent, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  List,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import lightTheme from '@/src/styles/themes/lightTheme';
import { ILooseObject } from '@/src/types/common.interface';

interface ISearchBarProps {
  getSearchResult: (query: string) => Promise<ILooseObject>;
  minChar: number;
  handleClickOnList: (value: ILooseObject) => void;
  optionLabelName: string;
  searchableText: string;
  optionKeyName: string;
  handleSearchableInputText: (value: string) => void;
  optionDetailsKeyNames?: string[];
  debounceTime?: number;
}

const SearchBar = (props: ISearchBarProps) => {
  const {
    getSearchResult,
    minChar,
    handleClickOnList,
    optionLabelName,
    searchableText,
    handleSearchableInputText,
    debounceTime = 500,
    optionKeyName,
    optionDetailsKeyNames
  } = props;

  const [searchResult, setSearchResult] = useState<ILooseObject>({});
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  function escapeRegExp(item: string) {
    return item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const debounceHandler = (value: string) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (value) {
        const data = await getSearchResult(value);
        setSearchResult(data);
        setIsOpen(true);
        setIsLoading(false);
      } else {
        setSearchResult([]);
      }
    }, debounceTime);
  };

  const handleInput = (event: SyntheticEvent, value: string) => {
    // Handle case when value is the string "undefined"
    const safeValue = value === 'undefined' ? '' : value;
    handleSearchableInputText(safeValue);
    if (safeValue && safeValue?.length >= minChar) {
      setIsLoading(true);
      debounceHandler(safeValue);
    } else {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const handleListClick = (event: SyntheticEvent, value: ILooseObject, reason: string) => {
    if (reason === 'selectOption') {
      handleClickOnList(value);
    }
  };

  const handleMatchedString = (value: string, input: string) => {
    const query: string[] = input.split(' ');
    const parsedString: ILooseObject[] = [];
    const allMatched: ILooseObject[] = [];
    query.forEach((item: string) => {
      const escapedString: string = escapeRegExp(item);
      const regEx = new RegExp(escapedString, 'gi');
      const matchedString = RegExp(regEx)?.exec(value);
      if (matchedString) {
        allMatched.push(matchedString);
      }
    });
    allMatched.sort((a: ILooseObject, b: ILooseObject) => {
      return a?.index - b?.index;
    });

    const allSplitted = allMatched.length > 0 ? splitByMany(allMatched, value) : [value];

    allSplitted?.map((splittedValue: string, index: number) => {
      parsedString.push({
        text: splittedValue,
        highlight: false,
        id: uuidv4()
      });
      parsedString.push({
        text: allMatched?.[index],
        highlight: true,
        id: uuidv4()
      });
    });

    return parsedString;
  };

  const splitByMany = (matchedArray: ILooseObject[], string: string): string[] => {
    const manyArgs = [...matchedArray];
    do {
      const arg = manyArgs.pop();
      string = string.replace(arg?.[0], ',');
    } while (manyArgs.length);
    return string.split(',');
  };

  const getOptionLabel = (option: ILooseObject) => {
    return option?.[optionLabelName];
  };

  const ListBox = (props: React.HTMLAttributes<HTMLUListElement>): JSX.Element => {
    const { children, ...rest } = props;
    return (
      <>
        <List {...rest} key="search_count">
          <Box display="flex" justifyContent="flex-end" mr={3}>
            <Typography>Total count : {searchResult?.page?.totalElements}</Typography>
          </Box>
        </List>
        <List {...rest} key="search_list">
          {children}
        </List>
      </>
    );
  };

  return (
    <Box>
      <Autocomplete
        disableClearable={true}
        onInputChange={handleInput}
        inputValue={searchableText}
        options={searchResult?.data ?? []}
        clearOnBlur={false}
        sx={{ width: 800 }}
        open={isOpen}
        filterOptions={(options) => options}
        onClose={() => {
          setIsOpen(false);
        }}
        onChange={(event: SyntheticEvent, value: ILooseObject, reason: string) =>
          handleListClick(event, value, reason)
        }
        getOptionLabel={(option: ILooseObject) => getOptionLabel(option)}
        ListboxComponent={ListBox}
        renderOption={(props, option, { inputValue }) => {
          // Use the custom renderOption if provided
          const matchedParts = handleMatchedString(option?.[optionLabelName], inputValue);

          return (
            <Box
              component="li"
              {...props}
              borderBottom={1}
              borderColor={lightTheme.palette.grey[300]}
              key={option?.[optionKeyName]}
            >
              <Box
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'flex-start'}
                alignItems={'flex-start'}
              >
                <Box>
                  {matchedParts.map((part: ILooseObject) => (
                    <Typography
                      key={part?.id}
                      component="span"
                      fontWeight={500}
                      color={
                        part?.highlight
                          ? lightTheme.palette.text.primary
                          : lightTheme.palette.text.secondary
                      }
                    >
                      {part?.text}
                    </Typography>
                  ))}
                </Box>
                <Box>
                  {optionDetailsKeyNames?.map((key: string) => (
                    <Typography
                      key={key}
                      component="span"
                      fontWeight={300}
                      color={lightTheme.palette.text.secondary}
                      fontSize={12}
                    >
                      {option?.[key]}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={`Enter at least ${minChar} characters`}
            variant="outlined"
            size="small"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleInput(event, searchableText);
              }
            }}
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: <>{!!isLoading && <CircularProgress size={20} />}</>
            }}
          />
        )}
      />
    </Box>
  );
};

export default SearchBar;
